import { configure, makeAutoObservable, runInAction } from 'mobx';
import { apiClient } from '../../shared/base-classes/apiBaseClass';
import notificationState from '../../shared/mobx/notification-state';
import auth from '../../shared/mobx/auth-state';


export interface PageData {
    current_page: number;
    data: keyObj[]; // Using keyObj interface here
    next_page: number | null;
    per_page: number;
    previous_page: number | null;
    total_records: number;
}

export interface IKeyPageData {
    current_page: number;
    data: IkeyUsageObj[];
    next_page: number | null;
    per_page: number;
    previous_page: number | null;
    total_records: number;
}

export interface IkeyUsageObj {
    id: number;
    authKeyId: number;
    source: string;
    url: string;
    createdAt: string; 
    updatedAt: string; 
    deleted: boolean;
    status: "Active" | "Inactive";
    loading?: boolean;
}


export interface keyObj {
    id: number;
    userId: number;
    key: string;
    createdAt: string;
    deleted: boolean;
    status: string;
    loading?: boolean;
    updatedAt?: string;
}

enum LoadingStatus {
    Idle = 'idle',
    InProcess = 'pending',
    Completed = 'complete',
    Failed = 'failed'
}

class ApiKeyState {
    apiClient = apiClient;
    loadingStatus = LoadingStatus.Idle;
    errorMessage = '';
    pageData: PageData | null = null;
    keyUsageData: IKeyPageData | null = null;

    constructor() {
        makeAutoObservable(this);
        // Changed from "never" to "always" for better state management discipline
        configure({ enforceActions: 'always' });
    }

    get keyList(): keyObj[] {
        return this.pageData?.data || [];
    }
    get usageList(): IkeyUsageObj[] {
        return  this.keyUsageData?.data || [];
    }

    get isLoading() {
        return this.loadingStatus === LoadingStatus.InProcess;
    }
    async refreshKeyList(options: { avoidClean?: boolean; noLoader?: boolean } = {}) {
        await this.getKeyList({ avoidClean: true, noLoader: true, ...options });
        if (this.keyList.length === 0) {
            this.generateKey();
        }
    }

    async getKeyList(options: { avoidClean?: boolean; noLoader?: boolean } = { avoidClean: false }): Promise<void> {
        try {
            runInAction(() => {
                if (!options.avoidClean) {
                    this.pageData = null;
                }
                if (!options.noLoader) {
                    this.loadingStatus = LoadingStatus.InProcess;
                }
            });

            const response = await this.apiClient.get<any, any>("user-authkeys");
            
            runInAction(() => {
                this.loadingStatus = LoadingStatus.Idle;
                // Handle nested response structure: response.data.data contains the array
                // After interceptor, response is already response.data from axios
                const responseData = response as any;
                
                // Check if response has nested data structure
                if (responseData?.data?.data && Array.isArray(responseData.data.data)) {
                    // Nested structure: { data: { data: [...], per_page: ... } }
                    this.pageData = {
                        current_page: responseData.data.current_page || 1,
                        data: responseData.data.data,
                        next_page: responseData.data.next_page,
                        per_page: responseData.data.per_page || 10,
                        previous_page: responseData.data.previous_page,
                        total_records: responseData.data.total_records || 0
                    };
                } else if (responseData?.data && Array.isArray(responseData.data)) {
                    // Flat structure: { data: [...], per_page: ... }
                    this.pageData = {
                        current_page: responseData.current_page || 1,
                        data: responseData.data,
                        next_page: responseData.next_page,
                        per_page: responseData.per_page || 10,
                        previous_page: responseData.previous_page,
                        total_records: responseData.total_records || 0
                    };
                } else {
                    // Fallback: treat entire response as PageData
                    this.pageData = (responseData as unknown as PageData) || null;
                }
            });

        } catch (error) {
            this.handleError(error as Error);
        }
    }

    async generateKey() {
        try {
            const response = await this.apiClient.post("generate-authkey/generate");
            // notificationState.addToasterSuccess(response.message || 'API Key generated successfully!');
            this.refreshKeyList();
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }
    
    async getKeyUsage(config: { 
        per_page?: number; 
        page?: number; 
        orderBy?: string; 
        orderByField?: string; 
        search?: string; 
    }): Promise<IKeyPageData | undefined> {
        try {
            // Fixed URL construction: proper query parameter format
            const keyId = this.keyList[0]?.id;
            if (!keyId) {
                throw new Error('No API key found');
            }
            const params = new URLSearchParams({
                per_page: String(config.per_page || 10),
                page: String(config.page || 1),
                orderBy: config.orderBy || 'desc',
                orderByField: config.orderByField || 'createdAt',
                search: config.search || ''
            });
            const response = await this.apiClient.get(`authKey-log-list/${keyId}?${params.toString()}`);
            return this.keyUsageData = response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

        async getUserKeyUsage(userId: number | string, config: { 
            per_page?: number; 
            page?: number; 
            orderBy?: string; 
            orderByField?: string; 
            search?: string; 
        }): Promise<IKeyPageData | undefined> {
        try {
            // Fixed URL construction: proper query parameter format
            const params = new URLSearchParams({
                per_page: String(config.per_page || 10),
                page: String(config.page || 1),
                orderBy: config.orderBy || 'desc',
                orderByField: config.orderByField || 'createdAt',
                search: config.search || ''
            });
            const response = await this.apiClient.get(`admin/authkey-log-list/${userId}?${params.toString()}`);
            return this.keyUsageData = response.data;
        } catch (error) {
            this.handleError(error);
        }
    }
    
    async resetKey(id: number) {
        const ind = this.keyList.findIndex(k => k.id === id);
        if (!this.pageData || ind === -1) return;
        try {
            runInAction(() => {
                if (this.pageData) {
                    this.pageData.data[ind].loading = true;
                }
            });
            const response = await this.apiClient.post<any>(`generate-authkey/reset`);
            runInAction(() => {
                if (this.pageData && response.data?.userAuthKey) {
                    this.pageData.data[ind] = response.data.userAuthKey;
                } else if (this.pageData && (response as any).userAuthKey) {
                    // Handle case where userAuthKey is at root level
                    this.pageData.data[ind] = (response as any).userAuthKey;
                }
            });
            notificationState.addToasterSuccess(response.message || 'API Key reset successfully!');
        } catch (error) {
            this.handleError(error);
        }
    }

    async toggleStatus(id: number) {
        const ind = this.keyList.findIndex(k => k.id === id);
        if (!this.pageData || ind === -1) return;
        try {
            runInAction(() => {
                if (this.pageData) {
                    this.pageData.data[ind].loading = true;
                }
            });
            const response = await this.apiClient.post(`status-change-authkey`, { authId: id });
     
            runInAction(() => {
                if (this.pageData) {
                    this.pageData.data[ind].status =
                        this.pageData.data[ind].status === "Active"
                            ? "InActive" : "Active";
                }
            });

            notificationState.addToasterSuccess(response.message || 'API Key status successfully!');

        } catch (error) {
            this.handleError(error);
        } finally {
            runInAction(() => {
                if (this.pageData) {
                    this.pageData.data[ind].loading = false;
                }
            });
        }
    }

    async  toggleUsageSourceStatus(id: number, status: Boolean) {
        const ind = this.usageList.findIndex(k => k.id === id);
        if (!this.keyUsageData || ind === -1) return;
        try {
            runInAction(() => {
                if (this.keyUsageData) {
                    this.keyUsageData.data[ind].loading = true;
                }
            });
            const response = await this.apiClient.post(
                 auth.isUser ?  `status-change-authkey-log` : `admin/status-change-authkey-log`,
                { id, status });
     
            runInAction(() => {
                if (this.keyUsageData) {
                    this.keyUsageData.data[ind].status =
                        this.keyUsageData.data[ind].status === "Active"
                            ? "Inactive" : "Active";
                }
            });
            notificationState.addToasterSuccess(response.message || 'API Key status successfully!');

        } catch (error) {
            this.handleError(error);
        } finally {
            runInAction(() => {
                if (this.keyUsageData) {
                    this.keyUsageData.data[ind].loading = false;
                }
            });
        }
    }
  

    async delete(id: number) {
        const ind = this.keyList.findIndex(k => k.id === id);
        if (!this.pageData || ind === -1) return;
        try {
            runInAction(() => {
                if (this.pageData) {
                    this.pageData.data[ind].loading = true;
                }
            });
            const response = await this.apiClient.post(`delete-authkey`, { authId: id });
            notificationState.addToasterSuccess(response.message || 'API Key Deleted successfully!');
            await this.refreshKeyList();
        } catch (error) {
            this.handleError(error);
        }
    }

    async updateKey(keyId: number, updatedData: keyObj) {
        try {
            const response = await this.apiClient.put(`updateapikey/${keyId}`, updatedData);
            notificationState.addToasterSuccess(response.message || 'API Key updated successfully!');
        } catch (error) {
            this.handleError(error);
        }
    }

    async statusChange(newStatus: LoadingStatus) {
        this.loadingStatus = newStatus;
    }


    resetState() {
        this.pageData = null;
        this.keyUsageData = null;
        this.loadingStatus = LoadingStatus.Idle;
        this.errorMessage = '';
    }

    handleError(error: Error | unknown): void {
        runInAction(() => {
            this.loadingStatus = LoadingStatus.Failed;
        });
        
        // Check if the error has a response with a specific structure
        const axiosError = error as { response?: { data?: { errors?: string[] } } };
        if (axiosError?.response?.data?.errors) {
            this.errorMessage = axiosError.response.data.errors.join(', ') || 'Something went wrong, try again...';
        } else {
            this.errorMessage = (error as Error).message || 'Something went wrong, try again...';
        }
        notificationState.addToasterError(this.errorMessage);
    }
}

const apiKeyState = new ApiKeyState();
export default apiKeyState;
